import type { ScaleLinear, ScaleTime } from 'd3-scale'
import { area, curveStepAfter } from 'd3-shape'
import { useMemo } from 'react'

import type { HourlyQuantileBand } from '@/features/bidding/components/cascading/charts/types'
import { getNextDatetime } from '@/features/bidding/components/cascading/charts/utils/getNextDatetime'

/** Band (p10-p90) opacity */
const BAND_OPACITY = 0.15

type QuantileBandsProps = {
  upBands: HourlyQuantileBand[] | null
  downBands: HourlyQuantileBand[] | null
  datetimes: Date[]
  timeScale: ScaleTime<number, number>
  valueScale: ScaleLinear<number, number>
  color: string
}

type ExpandedBand = {
  datetime: Date
  p10: number
  p90: number
}

/**
 * Renders quantile forecast bands as shaded background areas.
 * UP bands are positive (above zero), DOWN bands are negative (below zero).
 *
 * Shows the p10-p90 interval (80% confidence interval) for price uncertainty.
 */
function QuantileBands({ upBands, downBands, datetimes, timeScale, valueScale, color }: Readonly<QuantileBandsProps>) {
  const { upPath, downPath } = useMemo(() => {
    // Convert hourly bands to expanded format with proper datetime and negation
    const expandBands = (bands: HourlyQuantileBand[] | null, negate: boolean): ExpandedBand[] | null => {
      if (!bands || bands.length === 0) return null

      const multiplier = negate ? -1 : 1
      const expanded: ExpandedBand[] = []

      for (const band of bands) {
        const datetime = datetimes[band.hour]
        if (!datetime) continue

        // Ensure p10 < p90 (handle potentially inverted data)
        const low = Math.min(band.p10, band.p90)
        const high = Math.max(band.p10, band.p90)

        // For curveStepAfter, we need a point at each hour start
        // The curve will hold values horizontally until the next point
        expanded.push({
          datetime,
          p10: low * multiplier,
          p90: high * multiplier,
        })
      }

      // Add final point to extend the last hour to its end
      if (expanded.length > 0) {
        const lastBand = bands[bands.length - 1]
        const lastDatetime = datetimes[lastBand.hour]
        if (lastDatetime) {
          const endDatetime = getNextDatetime(datetimes, lastDatetime)
          const low = Math.min(lastBand.p10, lastBand.p90)
          const high = Math.max(lastBand.p10, lastBand.p90)
          expanded.push({
            datetime: endDatetime,
            p10: low * multiplier,
            p90: high * multiplier,
          })
        }
      }

      return expanded
    }

    const expandedUp = expandBands(upBands, false)
    const expandedDown = expandBands(downBands, true)

    // Create area generator for p10-p90 band
    const areaGen = area<ExpandedBand>()
      .x((d) => timeScale(d.datetime))
      .y0((d) => valueScale(d.p10))
      .y1((d) => valueScale(d.p90))
      .curve(curveStepAfter)

    return {
      upPath: expandedUp ? areaGen(expandedUp) : null,
      downPath: expandedDown ? areaGen(expandedDown) : null,
    }
  }, [upBands, downBands, datetimes, timeScale, valueScale])

  return (
    <g className="quantile-bands">
      {upPath && <path d={upPath} fill={color} opacity={BAND_OPACITY} />}
      {downPath && <path d={downPath} fill={color} opacity={BAND_OPACITY} />}
    </g>
  )
}

export default QuantileBands
